<script>
import BaseSimpleTable from '@/components/general/_base-simple-table.vue'
import BaseMixin from '@/mixins/baseMixin.js'
import DayOfWeek from '@/constants/date/DayOfWeekTitle.js'

export default {
  name: 'ScheduleTimeTable',
  components: { BaseSimpleTable },
  mixins: [BaseMixin],

  props: {
    scheduleData: {
      type: Array,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    validationError: {
      type: [String, Boolean],
      default: '',
    },
  },

  computed: {
    tableItems: {
      get() {
        return this.scheduleData
      },
      set(value) {
        this.$emit('update:removed', value)
      },
    },
    columns() {
      return this.headers.map(({ title }) => title)
    },
    allowedKeys() {
      return this.headers.map(({ value }) => value)
    },
    setTitlePlaceholder() {
      return this.tableItems.find((item) => item.name === DayOfWeek.WEDNESDAY)
        ?.id
    },
    tableConfig() {
      const titleKey = 'name'
      const baseConfig = {
        removeHeader: this.$t('general.closed'),
        removeIcon: 'mdi-close',
        softRemovalKeys: [titleKey],
        centeredHeaders: this.headers.filter(({ value }) => value !== titleKey),
        rowHeight: 100,
      }

      const sizeConfig = this.isExtraLarge
        ? {
            headerFontSize: '16px',
            removeTextSize: '18px',
            removeIconSize: '32px',
          }
        : {
            headerFontSize: '14px',
            removeTextSize: '16px',
            removeIconSize: null,
          }

      return { ...baseConfig, ...sizeConfig }
    },
  },
  methods: {
    updateSchedule(day, field, value) {
      this.$emit('update:times', { day: day.toLowerCase(), field, value })
    },
    validate() {
      return this.$refs.scheduleForm.validate()
    },
  },
}
</script>

<template>
  <v-form
    ref="scheduleForm"
    :class="[isLargeOrHigher ? 'schedule-table-width mx-auto' : 'w-100']"
  >
    <BaseSimpleTable
      :key="isLargeOrHigher"
      v-model="tableItems"
      v-bind="tableConfig"
      :headers="columns"
      :items="tableItems"
      removable
      :error-message="validationError"
      :allowed-keys="allowedKeys"
      class="my-4"
      gutters
    >
      <template v-slot:name-cell="{ item }">
        <div
          class="nowrap text-body-1 font-weight-medium text-capitalize row-title"
          :class="[
            { 'name-container': !isMobileViewPort },
            { 'lg-row-title': isExtraLarge },
          ]"
        >
          <span v-if="!item.removed">
            {{ isMobileViewPort ? item.shortName : item.name }}
          </span>
          <span v-else class="error--text text--darken-1">
            {{ $t('general.closed') }}
          </span>
        </div>
      </template>

      <template
        v-for="field in allowedKeys.filter((k) => k !== 'name')"
        v-slot:[`${field}-cell`]="{ item }"
      >
        <v-text-field
          :key="field"
          :value="item[field]"
          :disabled="item.removed"
          type="time"
          :rules="[(v) => item.validate(field, v)]"
          color="primary"
          class="pt-4 mx-auto time-field"
          :class="{
            'large-time-field-text large-time-field': isExtraLarge,
          }"
          @input="(value) => updateSchedule(item.name, field, value)"
        />
      </template>
    </BaseSimpleTable>
  </v-form>
</template>

<style lang="scss" scoped>
@import '@/scss/_vue-transitions.scss';

.time-field {
  width: 120px;

  &.large-time-field {
    width: 155px !important;
  }
}

.time-field ::v-deep input[type='time']::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.5;
}
.time-field ::v-deep .v-input__control input[type='time'] {
  text-align: center;
}

.time-field ::v-deep input {
  font-size: 20px !important;
  cursor: text !important;
}
.row-title {
  font-size: 18px !important;
}

.lg-row-title {
  font-size: 22px !important;
}

.break-select ::v-deep input {
  cursor: pointer !important;
}
.large-time-field-text ::v-deep input {
  font-size: 24px !important;
}

.name-container::before {
  display: block;
  height: 0;
  visibility: hidden;
  content: 'wednesday';
}
</style>
